import { BASE_URL } from '../Api';
import PluginHeader from "../PluginHeader";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { PluginDownloadButtons } from '../partials';

const vsmsDownloadUrls = {
  lite: `${BASE_URL}/plugins/taxcore-vanuatu/latest/Aronium.TaxCore.Vanuatu.Lite.exe`,
  pro: `${BASE_URL}/plugins/taxcore-vanuatu/latest/Aronium.TaxCore.Vanuatu.Pro.exe`,
};

const VsmsPlugin = () => {
  return (
    <>
      <HelmetProvider>
        <Helmet>
          <title>VSMS E-invoicing in Vanuatu - Aronium</title>
          <meta name="description" content="Prepare Aronium for Vanuatu Sales Monitoring System requirements using the VSMS plugin" />
        </Helmet>
      </HelmetProvider>

      <section className="py-8">
        <div className="max-w-screen-xl mx-auto text-center px-4">
          <h1 className="text-4xl mb-4">
            VSMS E-Invoicing in Vanuatu
          </h1>
          <p>
            Connect Aronium to Vanuatu's Sales Monitoring System and issue compliant electronic invoices directly from your POS.
          </p>
          <p className="mt-1 text-sm">
            Built for businesses to use V-SDC directly, or to connect Aronium with external E-SDC components.
          </p>

          <PluginDownloadButtons
            liteUrl={vsmsDownloadUrls.lite}
            proUrl={vsmsDownloadUrls.pro} />
        </div>
      </section>

      <div className="mx-auto mb-10 flex items-center justify-center">
          <img className="h-60" src={`${process.env.PUBLIC_URL}/images/vanuatu.png`} alt="Vanuatu Customs and Inland Revenue"/>
      </div>

      <div className="py-20 bg-[#1d4675]">
        <PluginHeader price="10"
          note={[ 
                "Supports V-SDC and E-SDC integrations."
          ]}  />
      </div>

      <div className="pt-24 pb-12">
        <div className="relative max-w-7xl mx-auto text-center px-8 lg:px-12">
          <h2 className="text-5xl text-gray-800 leading-tight mb-8">
            Why use <span className="font-semibold">VSMS</span> plugin?
          </h2>
            <p className="text-lg text-gray-600 mx-auto">
          Aronium's <span className="font-semibold">VSMS</span> plugin is designed to connect everyday POS operations with Vanuatu's fiscalization process. It helps sales, refunds, and invoices move through the required Sales Data Controller workflow so the business can issue compliant fiscal documents from Aronium.
          </p>
        </div>
      </div>

      <div className="mb-20 px-4">
        <div className="mx-auto max-w-3xl rounded-lg border border-gray-200 bg-white p-8 text-center shadow-sm">
          <p className="text-sm font-semibold uppercase tracking-wide text-sky-700">Official framework</p>
          <h3 className="mt-3 text-3xl font-semibold text-gray-800">Vanuatu Sales Monitoring System</h3>
          <p className="mt-4 text-gray-600">
            VSMS requires transactions to be recorded through accredited Electronic Fiscal Device components. The POS sends transaction data to a Sales Data Controller, which fiscalizes the invoice, returns the fiscal receipt data to the POS, and transmits audit data to VSMS.
          </p>
          <a
            href="https://customsinlandrevenue.gov.vu/taxes-and-licensing/vsms.html"
            className="button-rounded-white mt-8"
            target="_blank"
            rel="noreferrer"
          >
            Official VSMS information
          </a>
        </div>
      </div>

      <div className="pb-24">
        <div className="max-w-6xl mx-auto px-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8">
          <div className="bg-white p-8 shadow-lg rounded-lg border">
            <h3 className="text-2xl font-semibold text-gray-800">Accredited POS workflow</h3>
            <p className="mt-4 text-gray-600">
              Aronium is prepared for VSMS fiscalization flows where the POS communicates with an approved Sales Data Controller before the fiscal invoice is issued.
            </p>
          </div>
          <div className="bg-white p-8 shadow-lg rounded-lg border">
            <h3 className="text-2xl font-semibold text-gray-800">SDC ready-to-use. E-SDC integration-ready.</h3>
            <p className="mt-4 text-gray-600">
              Aronium VSMS supports direct V-SDC connection. For E-SDC setups, Aronium can integrate with third-party E-SDC software and hardware.
            </p>
          </div>
          <div className="bg-white p-8 shadow-lg rounded-lg border">
            <h3 className="text-2xl font-semibold text-gray-800">Fiscal invoice handling</h3>
            <p className="mt-4 text-gray-600">
              Transaction data is sent for fiscalization, then the fiscal response is used by Aronium so customers receive the required invoice or receipt details.
            </p>
          </div>
          <div className="bg-white p-8 shadow-lg rounded-lg border">
            <h3 className="text-2xl font-semibold text-gray-800">Ready for production use</h3>
            <p className="mt-4 text-gray-600">
              Install the released plugin and configure it with the SDC setup your business uses before issuing fiscal documents from Aronium.
            </p>
          </div>
        </div>
      </div>

      {/* Call to Action Section */}
      <div className="bg-gradient-to-tr from-[#23263d] to-sky-600 py-24 text-center text-white">
        <div className="relative mx-auto px-4">
          <h2 className="text-3xl md:text-6xl mb-6">
            Ready for VSMS?
          </h2>
          <p className="text-xl mb-10">
            Start using the <span className="font-semibold">VSMS</span> plugin to prepare Aronium for Vanuatu electronic fiscal reporting.
          </p>
          <a
                  href="https://help.aronium.com/hc/en-us/articles/36554760021010-Introduction-to-VSMS"
                  className="button-rounded-white px-12 py-4"
                  target="_blank"
                  rel="noreferrer"
              >
                  Learn more <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="ml-2 w-6 h-6 transition-all">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 8.25 21 12m0 0-3.75 3.75M21 12H3" />
                  </svg>
              </a>
        </div>
      </div>
    </>
  );
};

export default VsmsPlugin;
